import {normalizeIdCountry} from '@sollapay/domain';
import {PartyType} from '@sollapay/enums';

import type {BuyerEntryFormValues, BuyerPepFormValues} from '../validation';
import type {PepPublicRole, PepRelationship} from '@sollapay/enums';
import type {CreateBuyerEntry, CreatePepInput} from '@sollapay/types';

/** Shared by the person and organization branches — same PEP payload shape either way. */
export function buildPepPayload(
  pep: BuyerPepFormValues,
  isCorporate: boolean,
  entityName?: string
): CreatePepInput {
  if (!pep.isPep) return {isPep: false, parties: []};
  return {
    isPep: true,
    parties: pep.parties.map(p => ({
      relationship: isCorporate ? (p.relationship as PepRelationship) || null : null,
      fullName: entityName ?? p.fullName.trim(),
      countryOfOffice: p.countryOfOffice.trim(),
      publicRole: p.publicRole as PepPublicRole,
      stillInRole: p.stillInRole,
      roleStartMonth: p.roleStartMonth || null,
      roleEndMonth: p.stillInRole ? null : p.roleEndMonth || null,
      sourceOfWealth: p.sourceOfWealth.trim()
    }))
  };
}

/**
 * Maps roster form values to the wire payload's `buyers` array — shared by `useCreateBuyerForm`
 * and `useAddBuyersToPurchaseForm`. Uploaded documents (if any) attach to the first buyer in the
 * group, whichever type it is; omit `uploadedDocumentIds` for flows that don't collect documents.
 */
export function mapBuyerEntriesToPayload(
  buyers: BuyerEntryFormValues[],
  uploadedDocumentIds: string[] = []
): CreateBuyerEntry[] {
  return buyers.map((entry, i): CreateBuyerEntry => {
    const documentIds = i === 0 && uploadedDocumentIds.length > 0 ? uploadedDocumentIds : undefined;

    if (entry.partyType === PartyType.ORGANIZATION) {
      return {
        partyType: PartyType.ORGANIZATION,
        organization: {
          companyName: entry.organization.companyName,
          incorporationNumber: entry.organization.incorporationNumber,
          incorporationDate: entry.organization.incorporationDate,
          address: entry.organization.address,
          taxResidency: entry.organization.taxResidency,
          taxResidencyCountry: entry.organization.taxResidencyCountry || null,
          pep: buildPepPayload(entry.organization.pep, true)
        },
        signatories: entry.signatories,
        companyDocumentIds: entry.organization.companyDocumentIds,
        ...(documentIds ? {documentIds} : {})
      };
    }

    return {
      partyType: PartyType.PERSON,
      fullName: entry.person.fullName,
      nationalId: entry.person.nationalId,
      idType: entry.person.idType,
      idCountry: normalizeIdCountry(entry.person.idType, entry.person.idCountry),
      identityDocumentId: entry.person.identityDocumentId || undefined,
      dateOfBirth: entry.person.dateOfBirth,
      phone: entry.person.phone,
      email: entry.person.email,
      address: entry.person.address,
      taxResidency: entry.person.taxResidency,
      taxResidencyCountry: entry.person.taxResidencyCountry || null,
      pep: buildPepPayload(entry.person.pep, false, entry.person.fullName),
      ...(documentIds ? {documentIds} : {})
    };
  });
}
